// MagazinePage - Overview of all published articles

import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  BookOpen,
  Loader2,
  Search,
  MapPin,
  Heart,
  Briefcase,
  Sparkles,
  ExternalLink,
  Newspaper
} from 'lucide-react';
import {
  getPublishedArticles,
  ARTICLE_CATEGORIES,
  type Article,
  type ArticleCategory,
} from '../../services/articlesService';
import { ArticleCard } from './ArticleCard';

// Icon-Mapping für Kategorien
const CategoryIcon: React.FC<{ iconName: string; className?: string }> = ({ iconName, className = "w-4 h-4" }) => {
  const icons: Record<string, React.ReactNode> = {
    'MapPin': <MapPin className={className} />,
    'Heart': <Heart className={className} />,
    'Briefcase': <Briefcase className={className} />,
    'Sparkles': <Sparkles className={className} />,
    'ExternalLink': <ExternalLink className={className} />,
    'Newspaper': <Newspaper className={className} />,
  };
  return <>{icons[iconName] || null}</>;
};

interface MagazinePageProps {
  onBack: () => void;
  onArticleClick: (article: Article) => void;
}

export function MagazinePage({ onBack, onArticleClick }: MagazinePageProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<ArticleCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Load articles
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setIsLoading(true);
    try {
      console.log('[Magazin] Loading published articles...');
      const publishedArticles = await getPublishedArticles(undefined, 50);
      console.log('[Magazin] Loaded articles:', publishedArticles.length);
      setArticles(publishedArticles);
      setFilteredArticles(publishedArticles);
    } catch (error) {
      console.error('[Magazin] Error loading articles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter articles by category and search
  useEffect(() => {
    let result = articles;

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter((article) => article.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.excerpt.toLowerCase().includes(query) ||
          article.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    setFilteredArticles(result);
  }, [articles, selectedCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              <h1 className="font-semibold text-gray-900">Magazin</h1>
            </div>
            <div className="w-10" /> {/* Spacer */}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-3xl font-bold mb-3">GoodLife Magazin</h2>
          <p className="text-indigo-100 text-lg max-w-2xl">
            Inspiration, Tipps und Geschichten rund um gute Orte, gutes Leben und gute Arbeit.
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Artikel durchsuchen..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Category Filters */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                selectedCategory === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle
            </button>
            {ARTICLE_CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition flex items-center gap-1.5 ${
                  selectedCategory === cat.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <CategoryIcon iconName={cat.icon} className="w-4 h-4" />
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery || selectedCategory !== 'all'
                ? 'Keine Artikel gefunden'
                : 'Noch keine Artikel'}
            </h3>
            <p className="text-gray-500">
              {searchQuery || selectedCategory !== 'all'
                ? 'Versuche andere Suchbegriffe oder Filter.'
                : 'Schau bald wieder vorbei!'}
            </p>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <p className="text-sm text-gray-500 mb-6">
              {filteredArticles.length} {filteredArticles.length === 1 ? 'Artikel' : 'Artikel'}
              {selectedCategory !== 'all' && ` in ${ARTICLE_CATEGORIES.find(c => c.value === selectedCategory)?.label}`}
            </p>

            {/* Articles Grid */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  onClick={() => onArticleClick(article)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Custom scrollbar hide style */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}

export default MagazinePage;
